/**
 * WarmupProgressTab
 *
 * Shows per-account warmup progress across all (or a selected) agent.
 * Displays phase days completed, current phase, last run date, and status.
 *
 * Filters: VA Agent · Phase · Status · @username search
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../services/api';

// ─── helpers ──────────────────────────────────────────────────────────────────

function todayUTC() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function ProgressBar({ done, total, color = 'bg-indigo-500' }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden min-w-[40px]">
        <div className={`${color} h-full rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap">
        {done}/{total}d
      </span>
    </div>
  );
}

function PhaseBadge({ phase }) {
  if (phase === 'completed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
        ✓ Complete
      </span>
    );
  }
  if (phase === 'phase2') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
        Phase 2
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
      Phase 1
    </span>
  );
}

function StatusDot({ status }) {
  const MAP = {
    active:    'bg-emerald-500',
    completed: 'bg-blue-500',
    paused:    'bg-amber-400',
  };
  return <span className={`h-2 w-2 rounded-full flex-shrink-0 ${MAP[status] || 'bg-gray-400'}`} title={status} />;
}

const PHASE_OPTS = [
  { id: 'all',       label: 'All phases' },
  { id: 'phase1',    label: 'Phase 1' },
  { id: 'phase2',    label: 'Phase 2' },
  { id: 'completed', label: 'Completed' },
];

const STATUS_OPTS = [
  { id: 'all',       label: 'All statuses' },
  { id: 'active',    label: 'Active' },
  { id: 'completed', label: 'Completed' },
  { id: 'paused',    label: 'Paused' },
];

// ─── Main component ───────────────────────────────────────────────────────────

export default function WarmupProgressTab({ agents = [], initialAgentId = null }) {
  const [agentFilter, setAgentFilter] = useState(initialAgentId ? String(initialAgentId) : 'all');
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const LIMIT = 200;
  const searchTimer = useRef(null);

  const fetchProgress = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, page: pg };
      if (agentFilter !== 'all') params.agentId = agentFilter;
      if (phaseFilter !== 'all') params.phase = phaseFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (search.trim()) params.search = search.trim().replace(/^@/, '');

      const res = await api.get('/admin/va/ai-agents/warmup-progress', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setSummary(res.data.summary || null);
      setPage(pg);
    } catch (err) {
      console.error('WarmupProgressTab fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [agentFilter, phaseFilter, statusFilter, search]);

  // Refetch when filters change
  useEffect(() => { fetchProgress(1); }, [agentFilter, phaseFilter, statusFilter]);

  useEffect(() => {
    if (initialAgentId) setAgentFilter(String(initialAgentId));
  }, [initialAgentId]);

  // Debounce search
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchProgress(1), 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = todayUTC();

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Total accounts', value: summary.total, color: 'text-gray-900 dark:text-white' },
            { label: 'Phase 1 active', value: summary.phase1Active, color: 'text-indigo-600 dark:text-indigo-400' },
            { label: 'Phase 2 active', value: summary.phase2Active, color: 'text-amber-600 dark:text-amber-400' },
            { label: 'Completed', value: summary.completed, color: 'text-emerald-600 dark:text-emerald-400' },
            { label: 'Paused', value: summary.paused, color: 'text-gray-500 dark:text-gray-400' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-center">
              <div className={`text-2xl font-bold ${color}`}>{(value || 0).toLocaleString()}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {/* Agent filter */}
        <select
          value={agentFilter}
          onChange={(e) => setAgentFilter(e.target.value)}
          className="form-input text-sm py-1.5 pr-8"
        >
          <option value="all">All agents</option>
          {agents.filter((a) => a.automationType === 'warmup').map((a) => (
            <option key={a._id} value={a._id}>{a.name}</option>
          ))}
        </select>

        {/* Phase filter */}
        <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)} className="form-input text-sm py-1.5 pr-8">
          {PHASE_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>

        {/* Status filter */}
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="form-input text-sm py-1.5 pr-8">
          {STATUS_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>

        {/* Search */}
        <input
          type="text"
          placeholder="Search @username…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="form-input text-sm py-1.5 flex-1 min-w-[160px]"
        />

        <button onClick={() => fetchProgress(page)} disabled={loading} className="btn-secondary text-sm py-1.5">
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Account</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Agent</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Phase</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide w-40">Phase 1</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide w-40">Phase 2</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Last run</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {loading && items.length === 0 ? (
                <tr><td colSpan={7} className="py-10 text-center text-sm text-gray-400">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-sm text-gray-400">
                    No warmup progress records found.{' '}
                    {agentFilter === 'all' && phaseFilter === 'all' && statusFilter === 'all'
                      ? 'Start a Warmup Runner automation to begin tracking.'
                      : 'Try adjusting the filters.'}
                  </td>
                </tr>
              ) : (
                items.map((item) => {
                  const agentName = agents.find((a) => String(a._id) === String(item.agentId))?.name || '—';
                  const ranToday = item.lastRunDate === today;
                  const p2Enabled = item.phase2Target > 0;

                  return (
                    <tr key={String(item._id)} className="hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors">
                      {/* Account */}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <StatusDot status={item.status} />
                          <span className="font-medium text-gray-900 dark:text-white">
                            @{item.username || String(item.accountId).slice(-6)}
                          </span>
                          {ranToday && (
                            <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">ran today</span>
                          )}
                        </div>
                      </td>

                      {/* Agent */}
                      <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300 text-xs">{agentName}</td>

                      {/* Phase badge */}
                      <td className="px-4 py-2.5"><PhaseBadge phase={item.currentPhase} /></td>

                      {/* Phase 1 progress */}
                      <td className="px-4 py-2.5">
                        <ProgressBar
                          done={item.phase1DaysCompleted}
                          total={item.phase1Target}
                          color={item.phase1DaysCompleted >= item.phase1Target ? 'bg-emerald-500' : 'bg-indigo-500'}
                        />
                      </td>

                      {/* Phase 2 progress */}
                      <td className="px-4 py-2.5">
                        {p2Enabled ? (
                          <ProgressBar
                            done={item.phase2DaysCompleted}
                            total={item.phase2Target}
                            color={item.phase2DaysCompleted >= item.phase2Target ? 'bg-emerald-500' : 'bg-amber-500'}
                          />
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>

                      {/* Last run */}
                      <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {item.lastRunDate ? item.lastRunDate : '—'}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-2.5">
                        {item.status === 'completed' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                            Warmed up
                          </span>
                        ) : item.status === 'paused' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            Paused
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                            Active
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        {total > LIMIT && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-sm text-gray-500">
            <span>Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total.toLocaleString()}</span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1 || loading}
                onClick={() => fetchProgress(page - 1)}
                className="btn-secondary text-xs py-1 px-3 disabled:opacity-40"
              >← Prev</button>
              <button
                disabled={page * LIMIT >= total || loading}
                onClick={() => fetchProgress(page + 1)}
                className="btn-secondary text-xs py-1 px-3 disabled:opacity-40"
              >Next →</button>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Progress updates once per calendar day (UTC) as accounts report back from the VPS. Paused accounts resume when you click Start Warmup again.
      </p>
    </div>
  );
}
